import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

export type ContractorRow = {
  profileId: string;
  vendorId: string;
  firstName: string;
  lastName: string;
  vendorName: string;
  allocations: ContractorAllocation[];
  totalBudgeted: number;
  totalActualPaid: number;
  variance: number;
  monthlyBreakdown: ContractorMonth[];
};

export type ContractorAllocation = {
  teamId: string;
  teamName: string;
  monthlyRate: number;
  allocationPct: number;
  effectiveCost: number;
};

export type ContractorMonth = {
  month: string;
  budgeted: number;
  actualPaid: number;
  variance: number;
};

export type ContractorReconciliation = {
  contractors: ContractorRow[];
  totalBudgeted: number;
  totalActualPaid: number;
  totalVariance: number;
  unlinkedPayments: UnlinkedPayment[];
};

export type UnlinkedPayment = {
  vendorId: string;
  vendorName: string;
  totalPaid: number;
  txCount: number;
};

export type ManagedContractor = {
  allocationId: string;
  profileId: string;
  firstName: string;
  lastName: string;
  email: string;
  teamId: string;
  teamName: string;
  monthlyRate: number;
  allocationPct: number;
  startDate: string | null;
  endDate: string | null;
};

export type TeamOption = {
  id: string;
  name: string;
};

const financeContractorsService = {
  async getReconciliation(
    startDate: string,
    endDate: string,
  ): Promise<ContractorReconciliation> {
    const orgId = getActiveOrgId();

    // 1. Get all vendors linked to staff profiles (not candidates)
    let vendorQuery = supabase
      .from('fx_vendors')
      .select('id, name, display_name, profile_id, profiles!inner(id, first_name, last_name, profile_type)')
      .not('profile_id', 'is', null)
      .in('profiles.profile_type', ['employee', 'both'])
      .eq('is_active', true);
    vendorQuery = applyOrgFilter(vendorQuery);
    const { data: contractorVendors } = await vendorQuery;

    if (!contractorVendors?.length) {
      return { contractors: [], totalBudgeted: 0, totalActualPaid: 0, totalVariance: 0, unlinkedPayments: [] };
    }

    const profileIds = contractorVendors.map((v: any) => v.profile_id).filter(Boolean);
    const vendorIds = contractorVendors.map((v: any) => v.id);

    // 2. Get team member allocations for these profiles
    let membersQuery = supabase
      .from('team_members')
      .select('profile_id, team_id, monthly_rate, allocation_pct, start_date, end_date, teams!inner(name)')
      .in('profile_id', profileIds);
    if (orgId) membersQuery = membersQuery.eq('teams.organization_id', orgId);
    const { data: membersData } = await membersQuery;

    // 3. Get actual payments (expense transactions) for these vendors in date range
    //    Include category to filter to payroll-related expenses only, avoiding
    //    non-payroll vendor expenses (e.g. software purchases) inflating "actual paid".
    const PAYROLL_CATEGORY_NAMES = new Set(['Contractor Payroll', 'Salaries & Wages']);
    let txQuery = supabase
      .from('fx_transactions')
      .select('vendor_id, amount, date, fx_categories(name)')
      .eq('type', 'expense')
      .in('vendor_id', vendorIds)
      .gte('date', startDate)
      .lte('date', endDate);
    txQuery = applyOrgFilter(txQuery);
    const { data: txDataRaw } = await txQuery;
    // Keep only transactions with a payroll category or no category (uncategorized may be payroll)
    const txData = (txDataRaw ?? []).filter((tx: any) => {
      const catName = (tx as any).fx_categories?.name;
      return !catName || PAYROLL_CATEGORY_NAMES.has(catName);
    });

    // Build months in range
    const startParts = startDate.split('-').map(Number);
    const endParts = endDate.split('-').map(Number);
    const allMonths: string[] = [];
    let y = startParts[0];
    let m = startParts[1];
    const endKey = `${endParts[0]}-${String(endParts[1]).padStart(2, '0')}`;
    let key = `${y}-${String(m).padStart(2, '0')}`;
    while (key <= endKey) {
      allMonths.push(key);
      m += 1;
      if (m > 12) { m = 1; y += 1; }
      key = `${y}-${String(m).padStart(2, '0')}`;
    }

    // Group payments by vendor + month
    const paymentsByVendor = new Map<string, Record<string, number>>();
    (txData ?? []).forEach((tx: any) => {
      const vid = tx.vendor_id;
      const month = tx.date?.substring(0, 7);
      if (!vid || !month) return;
      if (!paymentsByVendor.has(vid)) paymentsByVendor.set(vid, {});
      const map = paymentsByVendor.get(vid) as Record<string, number>;
      map[month] = (map[month] ?? 0) + parseFloat(tx.amount);
    });

    // Build contractor rows
    const contractors: ContractorRow[] = [];
    let grandBudgeted = 0;
    let grandActual = 0;

    for (const vendor of contractorVendors) {
      const v = vendor as any;
      const profile = v.profiles;
      if (!profile) continue;

      // Allocations for this contractor
      const memberRecords = (membersData ?? []).filter((rec: any) => rec.profile_id === v.profile_id);
      const allocations: ContractorAllocation[] = memberRecords.map((rec: any) => {
        const rate = rec.monthly_rate != null ? parseFloat(rec.monthly_rate) : 0;
        const pct = rec.allocation_pct ?? 100;
        return {
          teamId: rec.team_id,
          teamName: (rec as any).teams?.name ?? 'Unknown',
          monthlyRate: rate,
          allocationPct: pct,
          effectiveCost: rate * (pct / 100),
        };
      });
      const payments = paymentsByVendor.get(v.id) ?? {};

      // Monthly breakdown
      const monthlyBreakdown: ContractorMonth[] = allMonths.map((month) => {
        // Check if member was active during this month
        let budgeted = 0;
        for (const mem of memberRecords) {
          const memberM = mem as any;
          const rate = memberM.monthly_rate != null ? parseFloat(memberM.monthly_rate) : 0;
          if (rate === 0) continue;
          const pct = (memberM.allocation_pct ?? 100) / 100;
          const memberStart = memberM.start_date ? new Date(memberM.start_date) : new Date('2020-01-01');
          const memberEnd = memberM.end_date ? new Date(memberM.end_date) : new Date('2099-12-31');
          const [yr, mo] = month.split('-').map(Number);
          const monthStart = new Date(yr, mo - 1, 1);
          const monthEnd = new Date(yr, mo, 0);
          if (monthEnd >= memberStart && monthStart <= memberEnd) {
            budgeted += rate * pct;
          }
        }

        const actualPaid = Math.round((payments[month] ?? 0) * 100) / 100;
        budgeted = Math.round(budgeted * 100) / 100;

        return {
          month,
          budgeted,
          actualPaid,
          variance: Math.round((actualPaid - budgeted) * 100) / 100,
        };
      });

      const totalBudgeted = monthlyBreakdown.reduce((s, row) => s + row.budgeted, 0);
      const totalActualPaid = monthlyBreakdown.reduce((s, row) => s + row.actualPaid, 0);

      grandBudgeted += totalBudgeted;
      grandActual += totalActualPaid;

      contractors.push({
        profileId: v.profile_id,
        vendorId: v.id,
        firstName: profile.first_name ?? '',
        lastName: profile.last_name ?? '',
        vendorName: v.display_name ?? v.name,
        allocations,
        totalBudgeted: Math.round(totalBudgeted * 100) / 100,
        totalActualPaid: Math.round(totalActualPaid * 100) / 100,
        variance: Math.round((totalActualPaid - totalBudgeted) * 100) / 100,
        monthlyBreakdown,
      });
    }

    // 4. Find unlinked payroll-category payments (vendors without profile_id)
    const PAYROLL_CATEGORIES = ['Contractor Payroll', 'Salaries & Wages'];
    let unlinkedQuery = supabase
      .from('fx_transactions')
      .select('vendor_id, amount, fx_vendors!inner(id, name, display_name, profile_id), fx_categories(name)')
      .eq('type', 'expense')
      .gte('date', startDate)
      .lte('date', endDate);
    unlinkedQuery = applyOrgFilter(unlinkedQuery);
    const { data: unlinkedData } = await unlinkedQuery;

    const unlinkedMap = new Map<string, { vendorName: string; totalPaid: number; txCount: number }>();
    (unlinkedData ?? []).forEach((tx: any) => {
      const catName = (tx as any).fx_categories?.name ?? '';
      if (!PAYROLL_CATEGORIES.includes(catName)) return;
      const vid = tx.vendor_id;
      if (!vid) return;
      // Skip vendors already linked to profiles
      if ((tx as any).fx_vendors?.profile_id) return;
      const existing = unlinkedMap.get(vid) ?? {
        vendorName: (tx as any).fx_vendors?.display_name ?? (tx as any).fx_vendors?.name ?? 'Unknown',
        totalPaid: 0,
        txCount: 0,
      };
      existing.totalPaid += parseFloat(tx.amount);
      existing.txCount += 1;
      unlinkedMap.set(vid, existing);
    });

    const unlinkedPayments: UnlinkedPayment[] = Array.from(unlinkedMap.entries())
      .map(([vendorId, data]) => ({
        vendorId,
        vendorName: data.vendorName,
        totalPaid: Math.round(data.totalPaid * 100) / 100,
        txCount: data.txCount,
      }))
      .sort((a, b) => b.totalPaid - a.totalPaid);

    return {
      contractors: contractors.sort((a, b) => b.totalBudgeted - a.totalBudgeted),
      totalBudgeted: Math.round(grandBudgeted * 100) / 100,
      totalActualPaid: Math.round(grandActual * 100) / 100,
      totalVariance: Math.round((grandActual - grandBudgeted) * 100) / 100,
      unlinkedPayments,
    };
  },

  async getManagedContractors(): Promise<ManagedContractor[]> {
    const orgId = getActiveOrgId();
    let query = supabase
      .from('team_members')
      .select('id, profile_id, team_id, monthly_rate, allocation_pct, start_date, end_date, profiles!inner(first_name, last_name, email, profile_type), teams!inner(name)')
      .or(`end_date.is.null,end_date.gte.${new Date().toISOString().split('T')[0]}`)
      .in('profiles.profile_type', ['employee', 'both'])
      .order('created_at', { ascending: false });
    if (orgId) query = query.eq('teams.organization_id', orgId);
    const { data, error } = await query;

    if (error) throw error;

    return (data ?? []).map((row: any) => ({
      allocationId: row.id,
      profileId: row.profile_id,
      firstName: row.profiles?.first_name ?? '',
      lastName: row.profiles?.last_name ?? '',
      email: row.profiles?.email ?? '',
      teamId: row.team_id,
      teamName: row.teams?.name ?? 'Unknown',
      monthlyRate: row.monthly_rate != null ? parseFloat(row.monthly_rate) : 0,
      allocationPct: row.allocation_pct ?? 100,
      startDate: row.start_date ?? null,
      endDate: row.end_date ?? null,
    }));
  },

  async getTeams(): Promise<TeamOption[]> {
    let query = supabase
      .from('teams')
      .select('id, name')
      .order('name');
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    return (data ?? []).map((t: any) => ({ id: t.id, name: t.name }));
  },

  async upsertAllocation(payload: {
    allocationId?: string;
    profileId: string;
    teamId: string;
    monthlyRate: number;
    allocationPct: number;
    startDate: string;
    endDate?: string | null;
  }): Promise<void> {
    if (payload.allocationId) {
      const { error } = await supabase
        .from('team_members')
        .update({
          team_id: payload.teamId,
          monthly_rate: payload.monthlyRate,
          allocation_pct: payload.allocationPct,
          start_date: payload.startDate,
          end_date: payload.endDate || null,
        })
        .eq('id', payload.allocationId);
      if (error) throw error;
    } else {
      // Guard against duplicate active allocation for same profile + team
      const today = new Date().toISOString().split('T')[0];
      const { data: existing } = await supabase
        .from('team_members')
        .select('id')
        .eq('profile_id', payload.profileId)
        .eq('team_id', payload.teamId)
        .or(`end_date.is.null,end_date.gte.${today}`)
        .limit(1);

      if (existing && existing.length > 0) {
        throw new Error('An active allocation already exists for this contractor and team. Please edit the existing allocation instead.');
      }

      const { error } = await supabase
        .from('team_members')
        .insert({
          profile_id: payload.profileId,
          team_id: payload.teamId,
          monthly_rate: payload.monthlyRate,
          allocation_pct: payload.allocationPct,
          start_date: payload.startDate,
          end_date: payload.endDate || null,
        });
      if (error) throw error;
    }
  },

  async removeAllocation(allocationId: string): Promise<void> {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('id', allocationId);
    if (error) throw error;
  },
};

export default financeContractorsService;
